import React from 'react';
import PropTypes from 'prop-types';
import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const FrVersion = ({ availableLanguages }) => {
  return (
    <>
      <h4 id='política-de-privacidad-aplicable-al-usuario-final'>
        Politique de confidentialité pour utilisateur final
      </h4>
      <p>Langues disponibles:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />
      <p>Date d’entrée en vigueur: 22 février 2024</p>
      <p>
        La confidentialité et la sécurité sont très importants pour nous au sein
        de Plaid. La présente Politique de confidentialité destinée aux
        utilisateurs finaux (la « Politique ») vise à vous aider (l’«
        utilisateur final ») à comprendre comment nous, chez Plaid, collectons,
        utilisons et partageons vos données lorsque vous utilisez des produits
        ou services Plaid (par exemple, lorsque vous utilisez Plaid Portal ou
        lorsque vous utilisez Plaid pour vous connecter et partager vos données
        afin que nous prenions en charge les applications (« applis ») que vous
        utilisez). Ces applis, qui proposent beaucoup de services et de produits
        destinés aux consommateurs, par exemple en vous aidant à épargner pour
        votre retraite, à gérer vos dépenses, à rationaliser vos demandes de
        crédit ou à effectuer des virements, sont conçues et vous sont fournies
        par nos clients professionnels (ici, nous les appellerons « développeurs
        ») et prises en charge par Plaid.
      </p>
      <p>
        Cette Politique s’applique à Plaid Inc., à Plaid Financial Ltd., et à
        Plaid B.V. (collectivement, « Plaid », « nous », « nos », « notre »).
        (Veuillez lire la section « Contacter Plaid » ci-dessous pour savoir
        laquelle de ces entités est responsable du traitement de vos données.)
        Cette politique ne couvre pas les services proposés par Plaid Consumer
        Reporting Agency, Inc. (« Plaid CRA »), pour plus d’informations sur
        cette politique, veuillez consulter la{' '}
        <Link href='https://plaid.com/plaid-check-consumer-report/privacy-policy/'>
          <a data-smooth-scroll data-offset='80'>
            Politique de confidentialité de CRA
          </a>
        </Link>
        .
      </p>
      <p>
        Vous devez lire attentivement cette Politique. Elle contient des
        informations importantes sur vos droits et vos choix en matière de vie
        privée.
      </p>
      <p>
        <em>Aller à la section:</em>
      </p>
      <p>
        <Link href='#tout-d’abord-le-contexte'>
          <a data-smooth-scroll data-offset='80'>
            Tout d’abord, le contexte
          </a>
        </Link>
      </p>

      <p>
        <Link href='#nos-pratiques-relatives-aux-donnes'>
          <a data-smooth-scroll data-offset='80'>
            Nos pratiques relatives aux données
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#données-que-nous-collectons-et-catégories-de-sources-de-données'>
              <a data-smooth-scroll data-offset='80'>
                Données que nous collectons et catégories de sources de données
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#comment-nous-utilisons-vos-donnees'>
              <a data-smooth-scroll data-offset='80'>
                Comment nous utilisons vos données
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#les-bases-juridiques-sur-lesquelles-nous-appuyons-pour-le-traitement'>
              <a data-smooth-scroll data-offset='80'>
                Les bases juridiques sur lesquelles nous nous appuyons pour le
                traitement (Utilisateurs au sein de l’EEE et du Royaume-Uni)
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#comment-nous-partageons-vos-données'>
              <a data-smooth-scroll data-offset='80'>
                Comment nous partageons vos données
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#nos-pratiques-en-matière-de-conservation-et-de-suppression'>
              <a data-smooth-scroll data-offset='80'>
                Nos pratiques en matière de conservation et de suppression
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#protection-des-données'>
              <a data-smooth-scroll data-offset='80'>
                Protection des données
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#quelques-derniers-dtails'>
          <a data-smooth-scroll data-offset='80'>
            Quelques derniers détails
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#transferts-internationaux-de-donnes'>
              <a data-smooth-scroll data-offset='80'>
                Transferts internationaux de données
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#vos-droits-en-matière-de-protection-des-données'>
              <a data-smooth-scroll data-offset='80'>
                Vos droits en matière de protection des données
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#les-contrôles-supplémentaires-dont-vous-disposez-en-matière-de-confidentialité'>
              <a data-smooth-scroll data-offset='80'>
                Les contrôles supplémentaires dont vous disposez en matière de
                confidentialité
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#modifications-apportes-à-cette-politique'>
              <a data-smooth-scroll data-offset='80'>
                Modifications apportées à cette Politique
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#contacter-plaid'>
              <a data-smooth-scroll data-offset='80'>
                Contacter Plaid
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#synthèses-des-activités-de-traitement'>
          <a data-smooth-scroll data-offset='80'>
            Synthèses des activités de traitement
          </a>
        </Link>
      </p>

      <ul>
        <li>
          <p>
            <Link href='#synthèses-des-activités-de-traitement-par-catégorie-de-donnée'>
              <a data-smooth-scroll data-offset='80'>
                Synthèses des activités de traitement par catégorie de donnée
              </a>
            </Link>
          </p>
        </li>
        <li>
          <p>
            <Link href='#synthèses-des-activités-de-traitement-par-produit'>
              <a data-smooth-scroll data-offset='80'>
                Synthèses des activités de traitement par produit
              </a>
            </Link>
          </p>
        </li>
      </ul>

      <p>
        <Link href='#avis-de-confidentialité-du-consommateur'>
          <a data-smooth-scroll data-offset='80'>
            Avis de confidentialité du consommateur
          </a>
        </Link>
      </p>

      <h5 id='tout-d’abord-le-contexte'>Tout d’abord, le contexte</h5>
      <p>
        <em>Un mot sur Plaid</em>
      </p>
      <p>
        Notre mission à Plaid est de dynamiser les innovateurs en leur donnant
        accès au système financier et débloquer la liberté financière pour tous.
        En fournissant un accès à des données de comptes financiers exploitables
        et de haute qualité que nous avons traduites et normalisées, nous
        permettons à nos développeurs d’applis et de services financiers de se
        concentrer sur la création d’expériences à votre bénéfice.
      </p>
      <p>
        <em>Au sujet de cette Politique</em>
      </p>
      <p>
        Notre objectif avec cette Politique est de fournir une explication
        simple et directe sur les données collectées par Plaid sur vous, et sur
        la façon dont nous utilisons et partageons ces informations. Nous
        attachons une attention particulière à la transparence et souhaitons
        vous donner une description claire et concise de la façon dont nous
        traitons vos données.
      </p>
      <p>
        Cette Politique ne couvre pas ce que les développeurs des applis que
        vous utilisez font de vos données. Vous devez lire les politiques de
        confidentialité ou les conditions de service de ces applis pour en
        savoir plus sur leurs pratiques. En outre, cette Politique ne couvre pas
        les données que nous collectons via nos sites Web ou lorsque vous
        interagissez avec Plaid en dehors de l’utilisation de nos produits ou
        services, par exemple en envoyant directement un e-mail à Plaid.
        Veuillez lire notre{' '}
        <Link href='#déclaration-de-confidentialité'>
          <a>Déclaration de confidentialité tous publics</a>
        </Link>{' '}
        et notre{' '}
        <Link href='#politique-relative-aux-cookies'>
          <a>Politique relative aux cookies</a>
        </Link>{' '}
        pour en savoir plus.
      </p>
      <p>
        Les services de Plaid ne s’adressent pas aux personnes âgées de moins de
        18 ans et nous ne collectons pas délibérément de données sur les
        enfants.
      </p>

      <h5 id='nos-pratiques-relatives-aux-donnes'>
        Nos pratiques relatives aux données
      </h5>
      <p>
        <strong>
          Plaid s’engage à fournir aux utilisateurs finaux un contrôle réel sur
          leurs données.
        </strong>{' '}
        Cette section décrit les pratiques de Plaid en matière de données, en
        particulier le traitement de données sur vous par nos soins. Nous
        fournissons également des synthèses de nos pratiques organisées par
        catégorie de données collectées et par produit à la fin de cette
        Politique, dans la section{' '}
        <Link href='#synthèses-des-activités-de-traitement'>
          <a>Synthèses des activités de traitement</a>
        </Link>
        .
      </p>

      <h5 id='données-que-nous-collectons-et-catégories-de-sources-de-données'>
        Données que nous collectons et catégories de sources de données
      </h5>
      <p>
        Comme décrit plus en détail ci-dessous, les données que nous collectons,
        utilisons et partageons dépendent des produits et services Plaid
        utilisés par vous et/ou de l’appli à laquelle vous vous êtes
        connecté(e). En fonction des produits ou services de Plaid utilisés par
        vous ou par les développeurs auxquels vous vous connectez, Plaid est
        susceptible de collecter ce qui suit:
      </p>
      <ul>
        <li>
          <p>les données que vous nous fournissez ;</p>
        </li>
        <li>
          <p>
            les données provenant des établissements financiers lorsque vous
            vous connectez à votre compte financier ;
          </p>
        </li>
        <li>
          <p>
            Les données provenant de l’appareil électronique que vous utilisez
            pour connecter à votre compte financier via Plaid, ou pour interagir
            autrement avec Plaid ;
          </p>
        </li>
        <li>
          <p>
            les données provenant du développeur de l’appli à laquelle vous vous
            êtes connecté(e) ;
          </p>
        </li>
        <li>
          <p>
            les Données provenant de nos sociétés affiliées visant à vous
            fournir une assistance, ou à vous offrir une meilleure expérience
            utilisateur ; et/ou
          </p>
        </li>
        <li>
          <p>
            les données provenant d’autres sources, notamment les prestataires
            de services et les services de vérification d’identité et de
            prévention de la fraude.
          </p>
        </li>
      </ul>
      <p>
        <strong>Les données que vous nous fournissez.</strong> Lorsque vous
        utilisez les produits ou services de Plaid (par exemple lorsque vous
        utilisez Plaid pour vous connecter à vos comptes financiers à une
        appli), nous collectons les données suivantes auprès de vous si
        nécessaire pour alimenter votre appli ou service particulier :
      </p>
      <ul>
        <li>
          <p>
            <strong>les éléments d’identification</strong> comme le nom,
            l’adresse e-mail, la date de naissance, le numéro de sécurité
            sociale et le numéro de téléphone ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données de connexion</strong> lorsque cela est exigé par
            le fournisseur de votre compte, par exemple votre identifiant et
            votre mot de passe, votre numéro de compte et le code d’acheminement
            ou un jeton de sécurité ;
          </p>
        </li>
        <li>
          <p>
            <strong>
              toute information supplémentaire nécessaire pour vous connecter à
              vos comptes
            </strong>
            , y compris les questions et réponses de sécurité, et le mot de
            passe à usage unique (OTP) et/ou
          </p>
        </li>
        <li>
          <p>
            des informations provenant de{' '}
            <strong>documents ou de relevés</strong>
            que vous fournissez à Plaid, comme des relevés bancaires ou des
            fiches de paie.
          </p>
        </li>
      </ul>
      <p>
        Lorsque vous fournissez des données de connexion et des informations
        supplémentaires nécessaires pour vous connecter à vos comptes, vous
        donnez également à Plaid la permission et l’autorité d’agir en votre nom
        pour accéder et transmettre des données à, et provenant de votre
        établissement financier.
      </p>
      <p>
        <strong>
          Les données que nous collectons auprès d’établissements financiers à
          propos et à partir de vos comptes.
        </strong>{' '}
        Suivant les produits ou services Plaid utilisés par vous ou par le
        développeur de l’appli que vous utilisez, et selon la nature et la façon
        dont les informations sont mises à disposition, nous sommes susceptibles
        de collecter les données suivantes auprès de vos établissements
        financiers :
      </p>
      <ul>
        <li>
          <p>
            <strong>les données de comptes</strong>, y compris le nom de
            l’établissement financier, le nom du compte, le type de compte, le
            titulaire du compte, le numéro de succursale, l’IBAN, le BIC, les
            numéros de compte, le code d’acheminement, et le code guichet ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données relatives au solde du compte</strong>, y compris
            le solde courant et disponible ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données relatives aux comptes de crédit</strong>, y
            compris les dates d’échéance, soldes dus, montants et dates des
            paiements, historique des transactions, plafond de crédit, statut
            des remboursements et taux d’intérêts ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données sur les comptes de prêt</strong>, y compris les
            dates d’échéance, statut des remboursements, soldes, montants et
            dates de paiement, taux d’intérêts, garants, type de prêt,
            échéanciers et durée ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données relatives aux comptes d’investissement</strong>,
            y compris les informations sur les transactions, type d’actifs,
            détails d’identification sur l’actif, quantité, prix, frais et base
            des coûts ;
          </p>
        </li>
        <li>
          <p>
            <strong>les éléments d’identification</strong> et les données sur
            le(s) titulaire(s) du compte, y compris nom, adresse mail, numéro de
            téléphone, date de naissance et adresse ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données concernant les transactions du compte</strong>,
            y compris le montant, la date, le bénéficiaire, type, quantité,
            prix, lieu, titres concernés et une description de la transaction ;
            et/ou
          </p>
        </li>
        <li>
          <p>
            <strong>les données de vos documents de paie et fiscaux</strong>, y
            compris les données concernant votre revenu et votre employeur,
            uniquement dans les cas où vous avez connecté vos comptes de paie ou
            que vous nous avez fourni votre fiche de paie ou fiche d’impôt.
          </p>
        </li>
      </ul>
      <p>
        Selon le service Plaid que vous ou le développeur de votre appli
        utilisez, et la manière dont les données sont mises à disposition, les
        données collectées à partir de vos comptes financiers peuvent inclure
        des données provenant de tous les comptes (par exemple, compte chèque,
        compte d’épargne, carte de crédit et comptes communs) accessibles par un
        seul ensemble d’identifiants de compte. Pour en savoir plus sur les
        données collectées en lien avec différents produits et services,
        consultez la{' '}
        <Link href='#synthèses-des-activités-de-traitement-par-produit'>
          <a>Synthèse des activités de traitement par produit</a>
        </Link>
        .
      </p>
      <p>
        <strong>
          Les données que nous recevons à partir de vos appareils.
        </strong>{' '}
        Lorsque vous utilisez un appareil, comme votre smartphone, votre
        tablette ou votre ordinateur, pour interagir avec nos services
        (notamment via l’appli d’un développeur), nous sommes susceptible de
        collecter les données suivantes sur cet appareil :
      </p>
      <ul>
        <li>
          <p>l’adresse de protocole Internet (IP) ;</p>
        </li>
        <li>
          <p>
            le réglage et l’emplacement du fuseau horaire, le lieu de l’appareil
            ;
          </p>
        </li>
        <li>
          <p>le modèle d’appareil et le système d’exploitation ;</p>
        </li>
        <li>
          <p>les fonctionnalités de nos services auxquelles vous accédez ;</p>
        </li>
        <li>
          <p>les données du navigateur ;</p>
        </li>
        <li>
          <p>les données réseau ; et</p>
        </li>
        <li>
          <p>
            d’autres données techniques concernant l’appareil (telles que les
            paramètres et les préférences).
          </p>
        </li>
      </ul>
      <p>
        <strong>
          Les données que nous recevons sur vous auprès des développeurs
          d’applis prises en charge par Plaid.
        </strong>{' '}
        Lorsque cela est nécessaire à la fourniture d’un service par Plaid
        (comme la vérification de votre identité, la protection contre la fraude
        ou le fait de rendre possible un virement), les développeurs des applis
        que vous utilisez peuvent nous fournir des éléments d’identification et
        des informations commerciales sur vous, comme votre nom, votre numéro de
        sécurité sociale, votre adresse e-mail, votre numéro de téléphone ou des
        informations sur vos comptes financiers et transactions.
      </p>
      <p>
        <strong>
          Données provenant de nos sociétés affiliées pour vous fournir une
          assistance, ou pour vous proposer une meilleure expérience
          utilisateur.
        </strong>{' '}
        Nous pouvons collecter des données auprès de nos sociétés affiliées (il
        s’agit de sociétés auxquelles nous sommes liés par une propriété ou un
        contrôle commun) afin de vous fournir un soutien ou d’améliorer votre
        expérience. Par exemple, si vous choisissez que Plaid CRA se souvienne
        des comptes financiers associés à votre numéro de téléphone, nous
        pouvons collecter les données suivantes auprès de Plaid CRA afin de vous
        aider à vous connecter à vos comptes plus rapidement lorsque vous
        utilisez nos services :
      </p>
      <ul>
        <li>
          <p>
            <strong>les éléments d’identification</strong> comme le nom,
            l’adresse e-mail et le numéro de téléphone ;
          </p>
        </li>
        <li>
          <p>
            <strong>les données de connexion</strong> lorsque cela est exigé par
            le fournisseur de votre compte, par exemple votre identifiant et
            votre mot de passe, le nom de la banque, votre numéro de compte et
            le code d’acheminement ou un jeton de sécurité ;
          </p>
        </li>
        <li>
          <p>
            si nécessaire,
            <strong>
              les données pour vérifier votre identité et vous connecter à vos
              comptes
            </strong>
            , notamment votre date de naissance, les questions et réponses de
            sécurité et le mot de passe unique (OTP) ; et/ou
          </p>
        </li>
        <li>
          <p>
            les données de votre appareil (comme expliqué plus en détail
            ci-dessus).
          </p>
        </li>
      </ul>
      <p>
        <strong>
          Les données que nous recevons sur vous provenant d’autres sources.
        </strong>{' '}
        Lorsque cela est nécessaire pour fournir un service, pour identifier
        votre appareil ou pour empêcher la fraude, les actes malveillants ou les
        menaces à la sécurité, nous sommes également susceptibles de recevoir
        des données sur vous directement auprès de tiers, par exemple votre
        opérateur sans fil, des mandataires et nos prestataires de services.
      </p>
      <p>
        <strong>
          Les informations que nous déduisons à partir des données que nous
          collectons.
        </strong>{' '}
        Nous sommes susceptibles de déduire des informations sur vous à partir
        des données que nous collectons. Par exemple, nous pouvons déduire votre
        géolocalisation, votre revenu annuel ou le type de compte ou de compte
        secondaire que vous avez choisi de connecter, par exemple lorsque vous
        connectez vos comptes de prêt, afin de permettre au développeur de
        savoir si le compte correspond à un emprunt hypothécaire, à un prêt
        étudiant ou à une carte de crédit.
      </p>

      <h5 id='comment-nous-utilisons-vos-donnees'>
        Comment nous utilisons vos données
      </h5>
      <p>
        Nous utilisons vos données aux fins professionnelles et commerciales
        suivantes :
      </p>
      <ul>
        <li>
          <p>
            <strong>Fournir des services :</strong> our faire fonctionner,
            fournir et maintenir nos services.
          </p>
        </li>
        <li>
          <p>
            <strong>Développer des services existants :</strong> pour améliorer,
            perfectionner, modifier, ajouter et développer continuellement nos
            services.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Aider à lutter contre la fraude, à vérifier votre identité ou à
              protéger la vie privée :
            </strong>{' '}
            pour vérifier votre identité et vous protéger, protéger nos
            développeurs, nos partenaires ou Plaid contre la fraude, les
            activités malveillantes et d’autres menaces liées à la vie privée et
            à la sécurité.
          </p>
        </li>
        <li>
          <p>
            <strong>Développer de nouveaux services :</strong> pour développer
            de nouveaux produits et services.
          </p>
        </li>
        <li>
          <p>
            <strong>Développer des idées :</strong> pour développer des
            informations basées sur les données que nous avons recueillies à
            votre sujet. Cela inclut vos données de transaction, d’autres
            données financières et les données provenant d’autres sources, pour
            aider les développeurs de vos applis connectées à fournir des
            services et/ou à vous proposer une meilleure expérience utilisateur,
            par exemple en vous donnant un accès plus rapide à vos fonds ou pour
            aider à la détection et à la lutte contre les activités
            potentiellement frauduleuses, ou à personnaliser leurs services à
            votre intention.
          </p>
        </li>
        <li>
          <p>
            <strong>Fournir un soutien :</strong> pour vous fournir une
            assistance, à vous ou à nos développeurs, y compris en vue de
            répondre à vos demandes relatives à nos services ou aux applis de
            nos développeurs.
          </p>
        </li>
        <li>
          <p>
            <strong>Communiquer avec vous :</strong> pour communiquer avec vous
            et vous envoyer des informations telles que des avis techniques, des
            mises à jour, des alertes de sécurité et des messages si vous
            utilisez Plaid Portal ou Remember Me.
          </p>
        </li>
        <li>
          <p>
            <strong>
              Enquêter sur les utilisations abusives et les manquements :
            </strong>{' '}
            pour enquêter sur les utilisations incorrectes de nos services ou
            des applis de nos développeurs, y compris les violations de la{' '}
            <Link href='#politique-relative-au-développeur'>
              <a>Politique relative au développeur</a>
            </Link>
            , activité criminelle ou autre accès non autorisé à nos services.
          </p>
        </li>
        <li>
          <p>
            <strong>Pour des raisons légales :</strong> pour nous conformer aux
            obligations contractuelles et légales en vertu du droit applicable
            et à d’autres fins légales telles que l’établissement et la défense
            contre des réclamations.
          </p>
        </li>
        <li>
          <p>
            <strong>Avec votre consentement :</strong> pour toute autre finalité
            ayant obtenu votre consentement sur vos instructions.
          </p>
        </li>
      </ul>
      <p>
        Nous collectons, utilisons et partageons des données qui ont été
        agrégées ou anonymisées de telle sorte qu'elles ne vous identifient pas
        personnellement, pour toute fin autorisée par la loi applicable. Cela
        inclut la création ou l’utilisation de données agrégées ou anonymisées
        pour développer de nouveaux services, ou pour faciliter la recherche, et
        à des fins d’analyse pour aider à évaluer la vitesse, l’exactitude et/ou
        la sécurité de nos services.
      </p>

      <h5 id='les-bases-juridiques-sur-lesquelles-nous-appuyons-pour-le-traitement'>
        Les bases juridiques sur lesquelles nous nous appuyons pour le
        traitement (Utilisateurs au sein de l’EEE et du Royaume-Uni)
      </h5>
      <p>
        Pour les personnes situées dans l’Espace économique européen (« EEE »)
        ou le Royaume-Uni, Plaid traite uniquement vos données personnelles
        lorsque nous pouvons nous appuyer sur une base juridique à cette fin. La
        base juridique sur laquelle nous nous appuyons pour traiter les données
        que nous recueillons dépendra des données que nous avons recueillies et
        de la finalité de leur traitement. En règle générale, nous ne
        recueillerons et ne traiterons des données que lorsque :
      </p>
      <ul>
        <li>
          <p>
            nous sommes liés par tout contrat ou accord avec vous (par exemple,
            pour nous conformer à nos contrats de services aux utilisateurs
            finaux) et devons respecter diverses exigences légales lorsque nous
            proposons des services réglementés, y compris nos services
            d’informations de compte et de paiement.
          </p>
        </li>
        <li>
          <p>
            nous exigeons que vos données soient conformes à nos obligations
            légales en vertu des lois en vigueur, pour protéger les droits
            légaux de Plaid, et pour prévenir et identifier les activités
            criminelles telles que le blanchiment de capitaux et la fraude. À
            ces fins, Plaid peut juger nécessaire de partager vos données à
            caractère personnel avec des entités telles que des tribunaux, des
            autorités publiques et des prestataires de services de lutte contre
            le blanchiment d’argent ;
          </p>
        </li>
        <li>
          <p>
            le traitement est nécessaire à nos intérêts légitimes pour maintenir
            efficacement l’intégrité de nos services. Cela inclut la
            récupération des informations du compte ou la fourniture de services
            d’initiation de paiement (ainsi que la vérification des résultats de
            cette activité), la communication avec vous et le fait de veiller à
            ce que Plaid, en tant qu’entité réglementée, respecte les normes
            attendues (y compris celles stipulées par l’autorité de conduite
            financière (Financial Conduct Authority) pour Plaid Financial Ltd et
            De Nederlandsche Bank pour Plaid B.V). ; ou
          </p>
        </li>
        <li>
          <p>vous nous avez donné votre consentement pour ce faire.</p>
        </li>
      </ul>
      <p>
        Dans la mesure où nous nous reposons sur ce consentement pour collecter
        et traiter vos données, vous avez le droit de retirer votre consentement
        à tout moment selon les instructions prévues dans la présente Politique.
      </p>

      <h5 id='comment-nous-partageons-vos-données'>
        Comment nous partageons vos données
      </h5>
      <p>Nous partageons vos données pour les raisons suivantes :</p>
      <ul>
        <li>
          <p>
            avec le développeur de l’appli que vous utilisez, et selon les
            instructions du développeur ;
          </p>
        </li>
        <li>
          <p>pour faire exécuter un contrat avec vous ;</p>
        </li>
        <li>
          <p>
            avec nos sous-traitants intervenant dans le traitement de vos
            données et autres prestataires de services, partenaires, mandataires
            ou sous-traitants concernant les services qu’ils réalisent pour nous
            ou pour nos développeurs ;
          </p>
        </li>
        <li>
          <p>
            avec les établissements financiers afin d’établir, de maintenir ou
            de gérer une connexion que vous avez choisi d’établir entre vos
            comptes auprès de votre établissement financier et une appli ;
          </p>
        </li>
        <li>
          <p>
            si nous estimons de bonne foi que la divulgation de vos Informations
            est appropriée pour se conformer à la loi, la réglementation ou les
            processus légaux applicables (tels qu’une ordonnance du tribunal ou
            une assignation) ;
          </p>
        </li>
        <li>
          <p>
            dans le cas d’une modification de propriété ou de contrôle de tout
            ou partie de notre activité (telle qu’une fusion, acquisition,
            réorganisation ou faillite) ;
          </p>
        </li>
        <li>
          <p>
            au sein de Plaid et entre Plaid et nos sociétés mères actuelles et
            futures, nos affiliés, filiales et autres sociétés sous contrôle et
            propriété commune ;
          </p>
        </li>
        <li>
          <p>
            parce que nous estimons que cela est raisonnablement approprié afin
            d’empêcher les activités malveillantes ou frauduleuses, ou autrement
            pour protéger vos droits, votre vie privée, votre sécurité ou vos
            biens ou ceux de développeurs, de nos partenaires, de Plaid et
            d’autres ; ou
          </p>
        </li>
        <li>
          <p>
            à toute autre fin déclarée, avec votre consentement ou selon vos
            instructions.
          </p>
        </li>
      </ul>
      <p>
        (Destiné aux utilisateurs situés aux États-Unis) Nous ne partageons pas
        vos données avec des tiers non affiliés, sauf dans la mesure autorisée
        par la loi (tel qu’autorisé par 12 C.F.R. § 1016.14 et 1016.15).
      </p>
      <p>
        Lorsque vous connectez vos comptes financiers via Plaid, nous utilisons
        généralement le service reCAPTCHA de Google pour nous aider à détecter
        la fraude et les activités malveillantes. Google reCAPTCHA est intégré
        aux produits et services Plaid afin de s’assurer que les saisies
        effectuées dans les formulaires en ligne, par exemple, sont
        effectivement effectuées par des personnes réelles et ne sont pas
        automatisées par des logiciels (ou des robots).
      </p>
      <p>
        Google reCAPTCHA analyse le comportement des utilisateurs de nos
        produits et services en utilisant différentes caractéristiques. Google
        reCAPTCHA traite des données à caractère personnel telles que votre
        adresse IP, votre durée de séjour sur notre site Web et d’autres
        informations sur votre utilisation des produits et services Plaid. Ces
        données seront transmises et stockées par Google sur des serveurs aux
        États-Unis et dans d’autres pays. Google a obtenu la certification
        UE-États-Unis Data Privacy Framework ; le certificat est disponible{' '}
        <Link href='https://www.dataprivacyframework.gov/s/participant-search/participant-detail?id=a2zt000000001L5AAI&status=Active'>
          <a>ici</a>
        </Link>
        . Lorsque reCAPTCHA est utilisé, la{' '}
        <Link href='https://policies.google.com/privacy?hl=fr'>
          <a>Politique de confidentialité</a>
        </Link>{' '}
        et les{' '}
        <Link href='https://policies.google.com/terms?hl=fr'>
          <a>Conditions d'utilisation de Google</a>
        </Link>{' '}
        s'appliquent à reCAPTCHA et aux informations que collecte Google via
        reCAPTCHA.
      </p>
      <p>
        Nous ne partageons vos données financières personnelles avec des tiers
        que pour permettre la fourniture des services que vous avez demandés,
        lorsque vous y consentez, et/ou pour vous protéger contre la fraude.
      </p>
      <p>
        Nous sommes susceptibles de collecter et de partager des données de
        cookies avec des tiers lorsque vous visitez notre site Web. Nous pouvons
        aussi autoriser des tiers à collecter ces données de cookies sur nos
        sites. Si vous souhaitez en savoir plus, nous vous renvoyons à notre{' '}
        <Link href='#politique-relative-aux-cookies'>
          <a>Politique relative aux cookies</a>
        </Link>{' '}
        et à la{' '}
        <Link href='https://policies.google.com/privacy?hl=fr'>
          <a>Politique de confidentialité</a>
        </Link>{' '}
        tous publics.
      </p>

      <h5 id='nos-pratiques-en-matière-de-conservation-et-de-suppression'>
        Nos pratiques en matière de conservation et de suppression
      </h5>
      <p>
        Nous conservons vos données uniquement le temps nécessaire. Pour
        déterminer si des données sont nécessaires, nous prenons en compte la
        raison pour laquelle vos données ont été collectées et utilisées et
        toute exigence légale de conserver vos données. Nous examinons
        régulièrement vos données pour veiller à ce qu’elles soient toujours
        nécessaires pour réaliser la finalité pour laquelle elles ont été
        collectées, ou pour satisfaire à toute autre exigence légale. Si un
        développeur supprime votre connexion entre son appli et vos données, les
        systèmes de Plaid sont conçus pour supprimer automatiquement vos données
        personnelles, hormis certaines exceptions où nous pourrions conserver
        vos informations.
      </p>
      <p>
        Les exceptions peuvent être les suivantes : (a) vous avez établi une
        connexion avec l’appli d’un autre développeur via Plaid qui est toujours
        active ; (b) Plaid a besoin de vos données pour continuer à vous fournir
        un service Plaid que vous avez sollicité ; (c) Plaid est tenu par la loi
        de conserver vos données ; (d) Plaid a besoin de vos données à des fins
        de lutte contre la fraude ou de protection de la vie privée, pour
        fournir une assistance ou enquêter sur des détournements ou des
        manquements ; ou (e) lorsque Plaid a anonymisé vos données de telle
        sorte qu'elles ne puissent pas être réidentifiées ou (f) nous vous
        demandons (et vous acceptez expressément) de nous permettre de conserver
        vos données plus longtemps.
      </p>
      <p>
        Vos données ne seront traitées que selon les exigences de la loi ou
        conformément à la présente Politique.
      </p>
      <p>
        Veuillez vous référer à la section relative à « Vos droits à la
        protection de vos données » pour les options qui sont mises à votre
        disposition, y compris la façon de demander la suppression de vos
        données. La section « Vos contrôles en matière de vie privée » dans
        cette Politique contient également des informations sur les outils à
        votre disposition pour consulter et gérer les connexions que vous avez
        établies via Plaid. Vous pouvez également nous contacter au sujet de nos
        pratiques de conservation des données en utilisant les informations de
        contact ci-dessous.
      </p>

      <h5 id='protection-des-données'>Protection des données</h5>
      <p>
        Les politiques et pratiques de sécurité de Plaid sont conçues pour
        protéger la confidentialité et l’intégrité de vos données. Plaid met en
        œuvre des contrôles conçus pour limiter l’accès à ces données au
        personnel qui a une raison professionnelle de les connaître et interdit
        à son personnel d’accéder, d’utiliser ou de divulguer ces données
        lorsque cela est contraire à la loi.
      </p>

      <h5 id='quelques-derniers-dtails'>Quelques derniers détails</h5>

      <h5 id='transferts-internationaux-de-donnes'>
        Transferts internationaux de données
      </h5>
      <p>
        Nous opérons dans le monde entier et de ce fait, nous transférons les
        données que nous collectons sur vous au-delà des frontières à des fins
        de traitement et de stockage (par exemple, nous transférons des données
        l’EEE et du Royaume-Uni vers les États-Unis et stockons des données dans
        des régions AWS situées aux États-Unis). Lorsque nous transférons des
        données vers un autre pays ou territoire, nous respectons ce faisant les
        lois de protection des données en vigueur. En particulier, lorsque nous
        transférons des données de l’EEE ou du Royaume-Uni vers d’autres pays,
        nous nous appuyons sur des décisions d’adéquation, des accords de
        transfert de données ou sur d’autres mécanismes approuvés (le cas
        échéant) par la Commission européenne ou le Secrétaire d’État du
        Royaume-Uni, notamment les clauses contractuelles types. Vous pouvez
        demander un exemplaire de ces clauses contractuelles types en nous
        contactant comme indiqué ci-dessous. Avant de transférer des données à
        partir de l’EEE ou du Royaume-Uni, nous procédons à des évaluations
        d’impact du transfert et mettons en œuvre toute mesure supplémentaire
        pour nous assurer que les données transférées seront conservées
        conformément aux exigences de l’EEE et du Royaume-Uni.
      </p>

      <h5 id='vos-droits-en-matière-de-protection-des-données'>
        Vos droits en matière de protection des données
      </h5>
      <p>
        Indépendamment de l’endroit où vous vivez, nous respecterons les droits
        suivants liés à vos données personnelles, sous réserve de certaines
        limites et exceptions prévues par la loi, et vous ne ferez l’objet
        d’aucune discrimination en exerçant ces droits :
      </p>
      <ul>
        <li>
          <p>accéder aux données collectées sur vous ;</p>
        </li>
        <li>
          <p>
            demander à accéder à davantage de détails sur les catégories et
            certains éléments spécifiques relatifs aux informations personnelles
            que nous pouvons avoir collectés sur vous dans les 12 derniers mois
            (y compris les informations personnelles divulguées à des fins
            commerciales) ;
          </p>
        </li>
        <li>
          <p>
            demander, dans certaines circonstances, à ce que nous corrigions ou
            mettions à jour vos données qui sont inexactes ou incomplètes ;
          </p>
        </li>
        <li>
          <p>
            demander, dans certaines circonstances, que nous effacions ou
            restreignions le traitement de vos données ;
          </p>
        </li>
        <li>
          <p>
            vous opposer au traitement de vos données et ce, sous certaines
            conditions prévues par la loi ;
          </p>
        </li>
        <li>
          <p>
            lorsque le traitement de vos données est basé sur votre
            consentement, retirer ce consentement ;
          </p>
        </li>
        <li>
          <p>
            demander que nous vous communiquions vos données dans un format
            structuré, couramment utilisé et lisible par machine, afin que vous
            puissiez les transmettre à une autre société, lorsque cela est
            techniquement possible.
          </p>
          <ul>
            <li>
              <p>
                Veuillez noter que pour un relevé officiel de vos informations
                financières, vous devez faire cette demande directement à votre
                établissement financier.{' '}
              </p>
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Selon votre lieu de résidence, vous pouvez avoir le droit de déposer une
        plainte. Nous accueillons favorablement et apprécions la possibilité de
        traiter toute préoccupation que vous pourriez avoir et nous vous
        encourageons à nous contacter. En outre, et selon votre lieu de
        résidence, vous pouvez avoir le droit de déposer plainte à tout moment
        auprès de votre autorité de contrôle (en charge de la protection des
        données). Pour les utilisateurs qui se trouvent dans l’EEE, vous
        trouverez les coordonnées du{' '}
        <Link href='https://edpb.europa.eu/about-edpb/about-edpb/members_en'>
          <a>Conseil européen de la protection des données (EDPB)</a>
        </Link>
        , sur le site Web de l’EDPB. Pour les utilisateurs finaux qui se
        trouvent au Royaume-Uni, vous trouverez les coordonnées de l’
        <Link href='https://ico.org.uk/'>
          <a>Information Commissioner’s Office (ICO)</a>
        </Link>{' '}
        sur le site Web de l’ICO. Pour les utilisateurs finaux en Suisse, il
        s’agit du{' '}
        <Link href='https://www.edoeb.admin.ch/edoeb/en/home.html'>
          <a>
            Commissaire fédéral à la protection des données et à l’information
          </a>
        </Link>
        .
      </p>

      <p>
        Pour exercer tout droit dont vous pourriez disposer, vous pouvez
        soumettre une demande via notre{' '}
        <Link href='https://my.plaid.com/data-subject-request-form'>
          <a>formulaire en ligne</a>
        </Link>
        . Vous pouvez aussi nous contacter comme précisé dans la section «
        Contacter Plaid » ci-dessous, pour exercer vos droits en matière de
        protection des données. Il peut vous être demandé de fournir des
        informations supplémentaires pour confirmer votre identité avant que
        nous répondions à votre demande.
      </p>
      <p>
        Si nous recevons votre demande d’un agent autorisé, nous pouvons
        demander la preuve que cet agent dispose d’une autorisation écrite
        valable, par exemple une procuration, pour soumettre des demandes en
        votre nom.
      </p>
      <p>
        Nous prendrons en compte toutes les demandes et fournirons notre réponse
        dans une période de temps raisonnable (et dans la période requise par la
        loi applicable). Veuillez noter, cependant, que certaines données
        peuvent être exclues de ces demandes, par exemple si nous conservons les
        données pour nous conformer à nos obligations légales ou pour initier,
        exercer ou défendre une action légale.
      </p>
      <p>
        Pour Plaid Financial Ltd. comme pour Plaid, B.V., nous avons désigné un
        délégué à la protection des données (DPD) qui est également responsable
        des questions de supervision en lien avec cette Politique. Vous pouvez
        le contacter par e-mail à l’adresse{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>

      <h5 id='les-contrôles-supplémentaires-dont-vous-disposez-en-matière-de-confidentialité'>
        Les contrôles supplémentaires dont vous disposez en matière de
        confidentialité
      </h5>
      <p>
        Plaid a mis en place le{' '}
        <Link href='my.plaid.com'>
          <a>Plaid Portal</a>
        </Link>{' '}
        pour vous proposer une façon pratique et centralisée de consulter et de
        gérer les connexions que vous avez établies via Plaid.
      </p>
      <p>
        Si vous êtes situé(e) aux États-Unis, vous pouvez vous inscrire à Plaid
        Portal en vous rendant sur my.plaid.com, en vérifiant votre numéro de
        téléphone et votre adresse e-mail, et en créant un mot de passe. Une
        fois que vous vous serez inscrit à Plaid Portal, vous serez dirigé(e)
        vers un tableau de bord où s’afficheront vos comptes financiers que vous
        avez connectés aux applis de votre choix via Plaid, et les types de
        données partagées avec ces applis. En outre, le Plaid Portal vous
        fournit des contrôles pour mettre fin à des connexions entre des applis
        et vos comptes financiers et supprimer les données associées conservées
        dans les systèmes de Plaid.
      </p>

      <h5 id='modifications-apportes-à-cette-politique'>
        Modifications apportées à cette Politique
      </h5>
      <p>
        De temps à autre, nous mettons à jour ou modifions la présente
        Politique. Si nous réalisons des changements, nous mettrons en ligne la
        nouvelle politique sur le site web de Plaid à{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>{' '}
        et indiquerons la date d’entrée en vigueur de cette Politique. Nous
        informerons également nos développeurs de toute modification importante
        en lien avec nos accords avec les développeurs, car ces derniers peuvent
        être mieux positionnés pour vous informer des changements à la présente
        Politique. Si vous êtes un utilisateur actif de Plaid Portal, nous vous
        enverrons également un e-mail vous informant de la mise à jour.
      </p>

      <h5 id='contacter-plaid'>Contacter Plaid</h5>
      <p>
        Si vous avez des questions ou des réclamations concernant la présente
        Politique, ou à propos de nos pratiques relatives à la protection de la
        vie privée de manière générale, vous pouvez nous contacter à{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        ou par courrier à:
      </p>
      <p>
        <em>Si vous résidez en dehors de l’EEE ou du Royaume Uni:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Inc.
        <br />
        À l’attention de : Service juridique
        <br />
        PO Box 7775 #35278
        <br />
        San Francisco, Californie 94120-7775
        <br />
        U.S.A.
      </div>
      <br />
      <p>
        Pour Plaid Financial Ltd. comme pour Plaid, B.V., nous avons désigné un
        délégué à la protection des données (DPD) qui est également responsable
        des questions de supervision en lien avec cette Politique. Vous pouvez
        le contacter par e-mail à l’adresse{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <br />
      <p>
        <em>Si vous résidez au Royaume Uni:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid Financial Ltd.
        <br />
        À l’attention de : Service juridique
        <br />
        New Penderel House, 4th Floor
        <br />
        283-288 High Holborn
        <br />
        Londres, Royaume Uni, WC1V 7HP
      </div>
      <br />
      <p>
        <em>Si vous résidez au sein de l’EEE:</em>
      </p>
      <div className='ios-legal-address'>
        Plaid, B.V.
        <br />
        À l’attention de : Service juridique
        <br />
        Muiderstraat 1
        <br />
        1011PZ Amsterdam
        <br />
        Pays-Bas
      </div>
      <br />
      <br />

      <h5 id='synthèses-des-activités-de-traitement'>
        Synthèses des activités de traitement
      </h5>

      <h5 id='synthèses-des-activités-de-traitement-par-catégorie-de-donnée'>
        Synthèses des activités de traitement par catégorie de donnée
      </h5>
      <p>
        Pour nous permettre rendre nos pratiques encore plus transparentes, nous
        avons consolidé les informations fournies dans notre Politique ci-dessus
        dans un tableau qui met en correspondance les catégories de données que
        Paid collecte sur vous avec les sources de données, les utilisations des
        données et les catégories de destinataires avec lesquels Plaid partage
        ces données. Pour obtenir des descriptions plus détaillées de nos
        pratiques en matière de collecte, d’utilisation et de partage de
        données, veuillez consulter les sections correspondantes de la Politique
        ci-dessus.
      </p>

      <div className='eupp-container'>
        <table className='eupp-table'>
          <thead>
            <tr>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Catégorie de données à caractère personnel et exemples
                  </span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Source des données à caractère personnel</span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>Utilisations des données à caractère personnel</span>
                </p>
              </th>
              <th scope='col' colSpan='1' rowSpan='1'>
                <p>
                  <span>
                    Catégories de destinataires avec lesquels les données
                    personnelles peuvent être partagées
                  </span>
                </p>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Éléments d’identification</span>
                </p>
                <p>
                  <span>
                    Par exemple : nom, adresse e-mail, numéro de téléphone et
                    nom d’utilisateur
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Les données que vous fournissez</span>
                  </li>
                  <li>
                    <span>
                      Les données que nous collectons depuis vos comptes
                      financiers
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons à partir de vos appareils
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous auprès des
                      développeurs d’applis prises en charge par Plaid
                    </span>
                  </li>
                  <li>
                    <span>
                      Données provenant de nos sociétés affiliées pour vous
                      fournir des services ou une assistance
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous provenant d’autres
                      sources
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’appli(s) que vous utilisez, et selon les
                      instructions du ou des développeurs
                    </span>
                  </li>
                  <li>
                    <span>Établissements financiers</span>
                  </li>
                  <li>
                    <span>Prestataires de services de paiement</span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de prévention des fraudes
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de vérification d’identité
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                  <li>
                    <span>
                      Tierces personnes lorsque la loi l’exige, par exemple les
                      autorités policières ou d’autres autorités
                      gouvernementales
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Données financières</span>
                </p>
                <p>
                  <span>
                    par exemple : nom et numéro du compte financier, solde et
                    historique des transactions
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Les données que vous fournissez</span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous auprès des
                      développeurs d’applis prises en charge par Plaid
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous collectons depuis vos comptes
                      financiers
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous provenant d’autres
                      sources
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous déduisons des données que nous
                      collectons auprès de vous ou d’autres sources
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’application(s) que vous utilisez, et
                      selon les instructions du développeur
                    </span>
                  </li>
                  <li>
                    <span>Institutions financières</span>
                  </li>
                  <li>
                    <span>Prestataires de services de paiement</span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                    <span> </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de prévention des fraudes
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de vérification d’identité
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                  <li>
                    <span>
                      Tiers lorsque la loi l’exige, par exemple les autorités
                      policières ou d’autres autorités gouvernementales
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Données commerciales</span>
                </p>
                <p>
                  <span>
                    par exemple : les données permettant de savoir lesquels
                    parmi nos services vous utilisez via les applis de
                    développeurs et les dates et heures de vos utilisations
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Les données que nous collectons depuis vos comptes
                      financiers
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous auprès des
                      développeurs d’applis prises en charge par Plaid
                    </span>
                  </li>
                  <li>
                    <span>
                      Données provenant de nos sociétés affiliées pour vous
                      fournir des services ou une assistance
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous provenant d’autres
                      sources
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous déduisons des données que nous
                      collectons auprès de vous ou d’autres sources
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’application(s) que vous utilisez, et
                      selon les instructions du développeur
                    </span>
                  </li>
                  <li>
                    <span>Institutions financières</span>
                  </li>
                  <li>
                    <span>Prestataires de services de paiement</span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                    <span> </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de prévention des fraudes
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de vérification d’identité
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                  <li>
                    <span>
                      Tiers lorsque la loi l’exige, par exemple les autorités
                      policières ou d’autres autorités gouvernementales
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Données de localisation</span>
                </p>
                <p>
                  <span>
                    par exemple : paramètres de fuseau horaire et emplacement de
                    l’appareil
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Les données que nous recevons à partir de vos appareils
                    </span>
                  </li>
                  <li>
                    <span>
                      Données provenant de nos sociétés affiliées pour vous
                      fournir des services ou une assistance
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous déduisons des données que nous
                      collectons auprès de vous ou d’autres sources
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’application(s) que vous utilisez, et
                      selon les instructions du développeur
                    </span>
                  </li>
                  <li>
                    <span>Établissements financiers</span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                    <span> </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de prévention des fraudes
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                  <li>
                    <span>
                      Tiers lorsque la loi l’exige, par exemple les autorités
                      policières ou d’autres autorités gouvernementales
                    </span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Données professionnelles</span>
                </p>
                <p>
                  <span>
                    par exemple : des informations sur votre employeur et les
                    informations de paie
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Les données que vous fournissez</span>
                  </li>
                  <li>
                    <span>
                      Les données que nous collectons depuis vos comptes
                      financiers
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous recevons sur vous provenant d’autres
                      sources
                    </span>
                  </li>
                  <li>
                    <span>
                      Les données que nous déduisons des données que nous
                      collectons auprès de vous ou d’autres sources
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’application(s) que vous utilisez, et
                      selon les instructions du développeur
                    </span>
                  </li>
                  <li>
                    <span>Établissements financiers</span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                    <span> </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de prévention des fraudes
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de vérification d’identité
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Données d’activités des réseaux électroniques</span>
                </p>
                <p>
                  <span>
                    par exemple : le modèle de votre appareil et votre système
                    d’exploitation et les données de navigation
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Les données que nous recevons à partir de vos appareils
                    </span>
                  </li>
                  <li>
                    <span>
                      Données provenant de nos sociétés affiliées pour vous
                      fournir des services ou une assistance
                    </span>
                  </li>
                </ul>
                <p>
                  <span></span>
                </p>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’application(s) que vous utilisez, et
                      selon les instructions du développeur
                    </span>
                  </li>
                  <li>
                    <span>Établissements financiers</span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                    <span> </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                </ul>
              </td>
            </tr>
            <tr>
              <th scope='row' colSpan='1' rowSpan='1'>
                <p>
                  <span>Les informations que nous déduisons</span>
                </p>
                <p>
                  <span></span>
                </p>
                <p>
                  <span>
                    par exemple : nous sommes susceptibles de déduire votre
                    localisation à partir de votre adresse IP ou votre revenu
                    annuel à partir de vos fiches de paie
                  </span>
                </p>
              </th>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Les informations que nous déduisons des données que nous
                      collectons auprès de vous ou d’autres sources
                    </span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>Fournir des services</span>
                  </li>
                  <li>
                    <span>Développer des services existants</span>
                  </li>
                  <li>
                    <span>
                      Aider à la prévention de la fraude et à la protection de
                      la confidentialité et de la sécurité
                    </span>
                  </li>
                  <li>
                    <span>Développer de nouveaux services</span>
                  </li>
                  <li>
                    <span>Développer des idées</span>
                  </li>
                  <li>
                    <span>Fournir un soutien</span>
                  </li>
                  <li>
                    <span>
                      Enquêter sur les utilisations abusives et les violations
                    </span>
                  </li>
                  <li>
                    <span>Pour des raisons légales</span>
                  </li>
                  <li>
                    <span>Avec votre consentement</span>
                  </li>
                </ul>
              </td>
              <td colSpan='1' rowSpan='1'>
                <ul>
                  <li>
                    <span>
                      Développeur(s) d’application(s) que vous utilisez, et
                      selon les instructions du développeur
                    </span>
                  </li>
                  <li>
                    <span>Institutions financiers</span>
                  </li>
                  <li>
                    <span>Prestataires de services de paiement </span>
                  </li>
                  <li>
                    <span>
                      Partenaires et co-contractants en rapport avec les
                      services qu’ils fournissent pour nous ou pour les
                      développeurs
                    </span>
                  </li>
                  <li>
                    <span>Conseillers professionnels</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services qui aident Plaid à analyser les
                      données pour servir les fins commerciales de Plaid
                    </span>
                    <span> </span>
                  </li>
                  <li>
                    <span>Fournisseurs de services d’hébergement de cloud</span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de développement de logiciels
                    </span>
                  </li>
                  <li>
                    <span>
                      Prestataires de services de prévention des fraudes
                    </span>
                  </li>
                  <li>
                    <span>Entités du groupe Plaid</span>
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='synthèses-des-activités-de-traitement-par-produit'>
        Synthèses des activités de traitement par produit
      </h5>
      <p>
        Comme indiqué ci-dessus, les données que nous collectons et utilisons
        dépendent des produits et services Plaid que vous et le développeur
        utilisez. Afin que vous compreniez mieux quelles données sont collectées
        et utilisées pour tel produit ou tel service, nous avons compilé des
        synthèses de notre collecte de données et de l’utilisation que nous en
        faisons par produit. Certains de ces produits sont uniquement
        disponibles aux États-Unis et accessibles aux utilisateurs basés aux
        États-Unis, et d’autres produits sont uniquement disponibles pour les
        utilisateurs situés dans l’EEE et au Royaume-Uni. Si vous ne connaissez
        pas les produits ou services Plaid qu’utilisent vos applications
        connectées, veuillez contacter le développeur de l’application. (À noter
        que cette liste reprend l’ensemble de produits de Plaid à la date
        d’entrée en vigueur de cette Politique et peut ne pas inclure des
        produits ou services en cours de développement à cette date.)
      </p>
      <p>
        En outre, la loi sur la protection des données dans certaines
        juridictions, telles que le Royaume-Uni et l’UE, fait la distinction
        entre le « responsable du traitement » et le « sous-traitant » des
        données. En fonction du produit ou service que vous et votre développeur
        utilisez, Plaid peut être le responsable du traitement ou le
        sous-traitant des données collectées, en vertu des lois en vigueur en
        matière de protection des données. Sauf indication contraire dans la
        description du produit ci-dessous, Plaid est le responsable du
        traitement des données à caractère personnel collectées.
      </p>

      <h5>Assets</h5>
      <p>
        Assets vous permet de fournir un accès à une vue à un instant t de vos
        données de comptes financiers choisies (par exemple les soldes de
        compte, les historiques de transaction et les informations sur
        l’identité du titulaire du compte) à Plaid et aux applis et services de
        votre choix. Nous collectons les éléments d’identification, les
        informations financières, les données commerciales, les données de
        localisation, les données d’activité de réseau électronique et nous en
        déduisons des informations. Nous utilisons ces données tel que décrit
        dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Auth</h5>
      <p>
        Auth vous permet d’authentifier les données de vos comptes financiers et
        donne accès à votre compte et à vos codes d’acheminement à Plaid et aux
        applis et services de votre choix. Nous collectons les éléments
        d’identification, les données financières, les données commerciales, les
        données de localisation et les données d’activité de réseau
        électronique. Nous utilisons ces informations tel que décrit dans la
        section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Balance</h5>
      <p>
        Balance et ses fonctionnalités en option vous permettent de fournir un
        accès à vos soldes de comptes financiers en temps réel et/ou aux
        déductions dérivées des soldes de vos comptes et à d’autres informations
        de compte à Plaid et aux applis et services de votre choix, afin de leur
        permettre de vous aider à intervenir, par exemple en évitant de mettre
        votre compte à découvert avant d’effectuer un virement. Nous collectons
        les éléments d’identification, les données financières, les données
        commerciales, les données de localisation, et les données d’activité de
        réseau électronique et dans certains cas, nous en déduisons des
        informations. Nous utilisons ces données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Identité et correspondance d’identité</h5>
      <p>
        Identity vous permet de fournir un accès aux donnés du titulaire du
        Identity vous permet de fournir un accès aux donnés du titulaire du
        compte détenues par votre établissement financier (par exemple, votre
        nom, votre adresse e-mail, votre numéro de téléphone, et votre adresse
        e-mail) à Plaid et aux applis et services de votre choix pour leur
        permettre, entre autres, de vérifier votre identité ou de pré-remplir
        vos données de compte au sein de l’appli. Identity Match aide les
        applications et services que vous utilisez à vérifier votre identité par
        rapport aux données du titulaire du compte détenues par votre
        établissement financier. Nous collectons les éléments d’identification,
        les données financières, les données commerciales, les données de
        localisation et les données d’activité de réseau électronique. Nous
        utilisons ces données tel que décrit ci-dessus dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Vérification de la carte de débit</h5>
      <p>
        La vérification de la carte de débit vous permet de fournir un accès aux
        données du titulaire de votre compte, au solde du compte financier en
        temps réel et aux données de l’historique des transactions, afin de
        confirmer que la carte de débit que vous utilisez est liée à un compte
        bancaire que vous possédez. Nous collectons les éléments
        d’identification, les données financières, les données commerciales, les
        données de localisation, et les données d’activité de réseau
        électronique et dans certains cas, nous en déduisons des informations.
        Nous utilisons ces données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        de notre société affiliée.
      </p>

      <h5>Vérification et surveillance de l’identité</h5>
      <p>
        Identity Verification and Monitor (« IDV ») facilite la vérification
        d’identité, la détection de la fraude et les services de détection des
        développeurs sur les listes de surveillance. Lorsque nous fournissons
        les services Identity Verification and Monitor aux développeurs, nous
        collectons et traitons uniquement vos données en tant que prestataire de
        services pour ce développeur. Ainsi, les développeurs déterminent la
        finalité et les moyens par lesquels vos données sont traitées. Pour en
        savoir plus sur la façon dont nous traitons les données d’utilisateurs
        finaux pour les développeurs et sur instruction de ces derniers, afin de
        fournir les services Identity Verification and Monitor, veuillez lire la{' '}
        <Link href='https://cognitohq.com/privacy-statement'>
          <a>Déclaration de confidentialité de l’utilisateur final</a>
        </Link>{' '}
        d’IDV.
      </p>

      <h5>Le compte Plaid</h5>
      <p>
        vous permet de vous inscrire à une expérience utilisateur plus rapide en
        permettant à Plaid d’enregistrer vos connexions de compte financier et
        vos informations d’identité pour une intégration et une utilisation plus
        rapides des applications que vous avez choisies. Nous collectons les
        éléments d’identification, les données financières, les données
        commerciales, les données de localisation et les données d’activité de
        réseau électronique. Nous utilisons ces données tel que décrit dans la
        section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Income</h5>
      <p>
        Income vous permet de fournir un accès à vos données de revenu et
        d’emploi à Plaid et aux applis et services de votre choix. Nous
        collectons les éléments d’identification, les données financières, les
        données commerciales, les données de localisation, les données de revenu
        et d’autres données professionnelles, les données d’activité de réseau
        électronique et nous en déduisons des informations. Nous utilisons ces
        données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Investments</h5>
      <p>
        Investments vous permet de fournir un accès aux données à partir de vos
        comptes de retraite, de courtage, d’épargne d’études et d’épargne de
        santé à Plaid et aux applis ou services de votre choix, afin qu’ils
        puissent vous fournir des outils de gestion des finances personnelles et
        de patrimoine. Nous collectons les éléments d’identification, les
        données financières, les données commerciales, les données de
        localisation, les données professionnelles, les données d’activité de
        réseau électronique et nous en déduisons des informations. Nous
        utilisons ces données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Liabilities</h5>
      <p>
        Liabilities vous permet de fournir à Plaid et aux applis et services de
        votre choix un accès aux données financières de vos comptes de carte de
        crédit, de prêt hypothécaire et de prêt étudiant. Nous collectons les
        éléments d’identification, les données financières, les données
        commerciales, les données de localisation, les données d’activité de
        réseau électronique et nous en déduisons des informations. Nous
        utilisons ces données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Payment Initiation et Variable Recurring Payments</h5>
      <p>
        Payment Initiation permet aux utilisateurs de services dans l’UE et au
        Royaume-Uni d’effectuer des paiements ponctuels, récurrents ou variables
        récurrents sur des comptes. Il s’agit de services tels que les virements
        bancaires, les ordres permanents, les paiements récurrents variables et
        les paiements ponctuels ou récurrents à partir de comptes, comme les
        retraits et les remboursements. Variable Recurring Payments permet à nos
        utilisateurs situés dans l’UE et au Royaume-Uni d’authentifier un compte
        bancaire une fois, puis d’effectuer des paiements futurs sans avoir
        besoin d’authentification supplémentaire. Nous collectons les éléments
        d’identification, les données financières, les données commerciales, les
        données de localisation, les données d’activité de réseau électronique
        et nous en déduisons des informations. Nous utilisons ces données tel
        que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Payouts</h5>
      <p>
        Payouts permet aux utilisateurs de services dans l’UE et le Royaume-Uni
        d’effectuer facilement et instantanément des retraits et des
        remboursements à partir d’une appli ou d’un service que vous utilisez,
        en ouvrant et en alimentant au préalable un compte de monnaie
        électronique à partir duquel les fonds peuvent être envoyés aux
        utilisateurs qui demandent un paiement. Nous collectons les éléments
        d’identification, les données financières, les données commerciales, les
        données de localisation, les données d’activité de réseau électronique
        et nous en déduisons des informations. Nous utilisons ces données tel
        que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Plaid Portal</h5>
      <p>
        Plaid Portal est un outil de protection de la vie privée que vous pouvez
        utiliser pour contrôler les connexions avec les applis que vous avez
        établies via Plaid. Vous pouvez utiliser Plaid Portal pour afficher les
        connexions aux applis dans un tableau de bord unique, déconnecter des
        comptes financiers de certaines applis ou supprimer des données sur les
        systèmes de Plaid. Vous pouvez également utiliser Plaid Portal pour
        afficher et gérer leurs comptes Plaid. Nous collectons les éléments
        d’identification, les données financières, les données commerciales, les
        données de localisation et les données d’activité de réseau
        électronique. Nous utilisons ces données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Signal</h5>
      <p>
        Pour les clients situés aux Etats-Unis, Signal permet aux applis et aux
        services que vous utilisez de vous proposer une meilleure expérience
        utilisateur, par exemple en vous donnant un accès plus rapide à vos
        fonds et en réduisant le risque de fraude ou d’échec de paiement. Nous
        collectons les éléments d’identification, les données financières, les
        données commerciales, les données de localisation, les données
        d’activité de réseau électronique et nous en déduisons des informations.
        Nous utilisons ces données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Statements</h5>
      <p>
        Statements permet de partager des copies de vos relevés de comptes
        financiers avec Plaid et les applis et services que vous choisissez.
        Nous collectons les éléments d’identification, les données financières,
        les données commerciales, les données de localisation et les données
        d’activité de réseau électronique. Nous utilisons ces données tel que
        décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Transactions</h5>
      <p>
        Transactions et ses produits associés vous permet de fournir à Plaid et
        aux applis et services de votre choix un accès aux historiques de
        transactions sur vos comptes financiers. Ainsi, ils peuvent vous fournir
        des outils de gestion de vos finances personnelles, de gestion des notes
        de frais, de modélisation des flux de trésorerie et davantage. Nous
        sommes susceptibles de collecter des éléments d’identification, des
        données financières, des données commerciales, des données de
        localisation, des données d’activité de réseau électronique et nous
        tirons des conclusions et des informations financières de ces données.
        Nous utilisons ces données tel que décrit ci-dessus dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5>Transfer</h5>
      <p>
        Transfer vous permet d’autoriser un développeur à vous envoyer des
        paiements ou à percevoir des paiements de votre part, par le biais de
        virements bancaires, de façon ponctuelle ou récurrente. Cela inclut le
        fait de vérifier le compte vers lequel ou à partir duquel le paiement
        sera envoyé, en évaluant le risque que le paiement échoue ou soit
        frauduleux, en traitant les instructions de paiement de façon
        électronique et en fournissant une confirmation au développeur que le
        paiement a été effectué. Nous sommes susceptibles de collecter des
        éléments d’identification, des données financières, de localisation et
        les données d’activité de réseau électronique. Nous utilisons ces
        données tel que décrit dans la section{' '}
        <Link href='#comment-nous-utilisons-vos-donnees'>
          <a>Comment nous utilisons vos données</a>
        </Link>{' '}
        dans cette Politique.
      </p>

      <h5 id='avis-de-confidentialité-du-consommateur'>
        Avis de confidentialité du consommateur
      </h5>
      <p>Dernière mise à jour : 22 février 2024</p>

      <table className='eupp-table'>
        <thead>
          <tr>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span></span>
              </p>
            </td>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  QUE FAIT PLAID DE VOS INFORMATIONS PERSONNELLES ?
                </strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Pourquoi ?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Les sociétés financières choisissent la façon dont elles
                  partagent vos informations personnelles. Les lois fédérales
                  donnent aux consommateurs le droit de limiter ce partage, du
                  moins en partie. Les lois fédérales exigent également de nous
                  que nous vous informions de la façon dont nous collectons,
                  partageons et protégeons vos informations personnelles.
                  Veuillez lire attentivement cet avis afin de comprendre ce que
                  nous faisons.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Quoi ?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Les types d’informations personnelles que nous collectons et
                  partageons dépendent du produit ou service utilisés par vous,
                  ou par le développeur de l’appli à laquelle vous vous
                  connectez. Ces informations peuvent inclure :
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    Le numéro de sécurité sociale et les soldes de compte
                  </span>
                </li>
                <li>
                  <span>
                    Les transactions effectuées sur le compte et l’historique
                    des transactions
                  </span>
                </li>
                <li>
                  <span>
                    Les informations relatives au revenu et à l’emploi
                  </span>
                </li>
              </ul>
              <p>
                <span>
                  Pour en savoir plus sur les types d’informations personnelles
                  que nous collectons, veuillez lire la section{' '}
                  <Link href='#données-que-nous-collectons-et-catégories-de-sources-de-données'>
                    <a>Données que nous collectons ou déduisons</a>
                  </Link>{' '}
                  dans notre Politique de confidentialité de l’utilisateur
                  final. Lorsque vous ne faites plus partie de nos clients, nous
                  continuons à utiliser et à partager vos informations tel que
                  décrit dans cet avis.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Comment ?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Toutes les sociétés financières ont besoin de partager les
                  informations personnelles de leurs clients pour pouvoir
                  fournir leurs produits et services. Dans la section
                  ci-dessous, nous répertorions les raisons pour lesquelles les
                  sociétés financières sont susceptibles de partager les
                  informations personnelles de leurs clients, les raisons pour
                  lesquelles Plaid choisit de les partager et si vous pouvez ou
                  non limiter ce partage.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Les raisons pour lesquelles nous sommes susceptibles de
                  partager vos informations personnelles
                </strong>
              </p>
            </th>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>Plaid partage-t-il des informations ?</strong>
              </p>
            </th>
            <th scope='col' colSpan='1' rowSpan='1'>
              <p>
                <strong>Pouvez-vous limiter ce partage ?</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Pour servir nos finalités professionnelles ordinaires :
                </strong>
                <br />
                <span>
                  par exemple pour connecter et partager des données provenant
                  de vos comptes financiers afin de prendre en charge les applis
                  que vous utilisez, ainsi que pour vous protéger contre la
                  fraude ou les utilisations abusives, pour répondre aux
                  ordonnances des tribunaux et aux enquêtes judiciaires ou pour
                  communiquer des informations aux agences d’évaluation du
                  crédit.
                </span>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Oui</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Non</span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Pour servir nos finalités marketing</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Non</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nous ne partageons pas</span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Pour procéder à du marketing conjoint avec d’autres sociétés
                  financières
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Non</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nous ne partageons pas</span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Pour servir les finalités opérationnelles ordinaires de nos
                  sociétés affiliées : informations sur les transactions et les
                  expériences
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Oui</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Oui, et nous n’effectuons de partages qu’à votre demande
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Pour servir les finalités opérationnelles ordinaires de nos
                  sociétés affiliées : informations sur la solvabilité
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Non</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nous ne partageons pas</span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Pour permettre aux sociétés non affiliées de vous cibler à des
                  fins de marketing
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Non</span>
              </p>
            </td>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Nous ne partageons pas</span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Question</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Contactez-nous à l’adresse{' '}
                  <Link href='mailto:privacy@plaid.com'>
                    <a>privacy@plaid.com</a>
                  </Link>
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Qui sommes-nous ?</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Qui fournit cet avis ?</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>Plaid Inc.</span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Ce que nous faisons</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Comment Plaid protège-t-il mes informations personnelles ?
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Pour protéger vos informations personnelles contre les accès
                  et utilisations non autorisés, nous prenons des mesures
                  conformes aux lois fédérales. Ces mesures comprennent des
                  mesures de protection informatiques et la sécurisation des
                  fichiers et des bâtiments.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Comment Plaid collecte-t-il mes informations personnelles ?
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Comme expliqué plus en détail dans la section{' '}
                  <Link href='#données-que-nous-collectons-et-catégories-de-sources-de-données'>
                    <a>Données que nous collectons ou déduisons</a>
                  </Link>{' '}
                  de notre Politique de confidentialité de l’utilisateur final,
                  nous collectons les informations personnelles que vous nous
                  fournissez et auprès d’autres sociétés lorsque vous utilisez
                  nos produits ou services. Cela inclut, par exemple, les cas où
                  vous utilisez Plaid Portal ou lorsque vous utilisez Plaid pour
                  connecter et partager vos données pour alimenter les
                  applications (« applis ») que vous utilisez.
                </span>
              </p>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>
                  Pourquoi je ne peux pas limiter toutes les activités de
                  partage ?
                </strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Les lois fédérales vous donnent le droit de limiter uniquement
                  :
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    le partage pour servir les finalités opérationnelles
                    ordinaires de nos sociétés affiliées : informations sur
                    votre solvabilité ;
                  </span>
                </li>
                <li>
                  <span>
                    l’utilisation par les sociétés affiliées de vos informations
                    afin de vous cibler à des fins de marketing ;
                  </span>
                </li>
                <li>
                  <span>
                    partager afin de permettre aux sociétés non affiliées de
                    vous cibler à des fins de marketing.
                  </span>
                </li>
              </ul>
              <p>
                <span></span>
              </p>
              <p>
                <span>
                  Les lois d’État et les sociétés individuelles peuvent vous
                  proposer des droits supplémentaires vous permettant de limiter
                  le partage d’informations. Pour en savoir plus sur vos droits
                  en vertu des lois d’État, voir ci-dessous.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <table className='eupp-table'>
        <thead>
          <tr>
            <th scope='col' colSpan='2' rowSpan='1'>
              <p>
                <strong>Définitions</strong>
              </p>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Sociétés affiliées</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Les sociétés apparentées qui sont détenues conjointement ou
                  sous contrôle commun. Il peut s’agir de sociétés financières
                  et non financières.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    <em>
                      Nos sociétés affiliées comprennent Plaid Financial Ltd.,
                      Plaid, B.V. et Plaid Consumer Reporting Agency Inc. (« ARC
                      Plaid »).
                    </em>
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Sociétés non affiliées</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Les sociétés non affiliées parce qu’elles ne sont pas détenues
                  conjointement ou ne sont pas sous contrôle commun. Il peut
                  s’agir de sociétés financières et non financières.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    <em>
                      Plaid ne partage d’informations avec des sociétés non
                      affiliées qui pourraient vous cibler à des fins de
                      marketing.
                    </em>
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <th scope='row' colSpan='1' rowSpan='1'>
              <p>
                <strong>Marketing conjoint</strong>
              </p>
            </th>
            <td colSpan='1' rowSpan='1'>
              <p>
                <span>
                  Un accord formel entre sociétés financières non affiliées qui
                  vous proposent conjointement des produits ou des services
                  financiers.
                </span>
              </p>
              <ul>
                <li>
                  <span>
                    <em>Plaid ne fait pas de marketing conjoint.</em>
                  </span>
                </li>
              </ul>
            </td>
          </tr>
          <tr>
            <td colSpan='2' rowSpan='1'>
              <p>
                <strong>Autres informations importantes</strong>
              </p>
            </td>
          </tr>
          <tr>
            <td colSpan='2' rowSpan='1'>
              <p>
                <strong>Californie : </strong>
                <span>
                  Si vous résidez en Californie, nous ne partagerons pas
                  d’informations personnelles que nous collectons sur vous, sauf
                  si les lois californiennes le permettent.
                </span>
              </p>
              <p>
                <strong>Vermont : </strong>
                <span>
                  Si vous résidez dans le Vermont, nous ne partagerons pas
                  d’informations personnelles que nous collectons sur vous avec
                  des sociétés non affiliées, sauf si la loi nous permet de le
                  faire ou si vous nous y autorisez.
                </span>
              </p>
            </td>
          </tr>
        </tbody>
      </table>

      <hr className='section-divider' />
    </>
  );
};

FrVersion.propTypes = propTypes;
export default FrVersion;
